import { z } from 'zod'
import { DETECTION_META } from './detection-meta.js'

/**
 * SPEC.md §4.1 — the frozen detection vocabulary.
 *
 * `DETECTION_KEYS` is the contract shared by the database (`detections.detection_key`),
 * the config (`ServerConfig.modules`), the Lua resource (`shared/detections.lua`,
 * generated) and the panel (`module.<key>.name` i18n keys). Adding or renaming a
 * key is a WP0 change that touches all five documents.
 */

export const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const
export const SeveritySchema = z.enum(SEVERITIES)
export type Severity = (typeof SEVERITIES)[number]

export const ACTIONS = ['log', 'kick', 'ban'] as const
export const ActionSchema = z.enum(ACTIONS)
export type Action = (typeof ACTIONS)[number]

export const SIDES = ['client', 'server', 'cloud'] as const
export const SideSchema = z.enum(SIDES)
export type Side = (typeof SIDES)[number]

export const CATEGORIES = ['player', 'weapon', 'combat', 'vehicle', 'world', 'integrity', 'network', 'connection'] as const
export const CategorySchema = z.enum(CATEGORIES)
export type Category = (typeof CATEGORIES)[number]

export const DETECTION_KEYS = [
  // client (38)
  'godmode',
  'health_over_max',
  'health_probe',
  'armor_over_max',
  'invisible',
  'noclip',
  'speed_hack',
  'teleport',
  'spectate',
  'freecam',
  'super_jump',
  'infinite_stamina',
  'night_vision',
  'blacklisted_ped',
  'blacklisted_weapon',
  'weapon_gained',
  'infinite_ammo',
  'explosive_ammo',
  'damage_modifier',
  'weapon_damage_meta',
  'silent_aim',
  'shot_cadence',
  'no_recoil',
  'vehicle_modifier',
  'vehicle_speed',
  'vehicle_handling',
  'vehicle_godmode',
  'vehicle_invisible',
  'vehicle_noclip',
  'vehicle_gravity',
  'player_blips',
  'entity_spam_client',
  'menu_texture',
  'menu_key',
  'resource_injection',
  'resource_stopped',
  'client_integrity',
  'client_honeypot',
  // server (32)
  'heartbeat_missing',
  'honeypot_event',
  'honeypot_export',
  'honeypot_command',
  'explosion_spam',
  'explosion_blacklist',
  'weapon_damage_event',
  'give_weapon_event',
  'remove_weapon_event',
  'server_headshot_ratio',
  'magic_bullet',
  'fire_spam',
  'clear_ped_tasks',
  'projectile_spam',
  'ptfx_blacklist',
  'entity_spawn_rate',
  'entity_blacklisted_model',
  'entity_unauthorized_script',
  'server_godmode',
  'server_super_jump',
  'server_freecam',
  'server_teleport',
  'server_speed',
  'chat_spam',
  'chat_blacklisted_string',
  'event_spam',
  'blacklisted_event',
  'safe_event_invalid',
  'event_rule',
  'resource_list_mismatch',
  'connect_offline',
  'custom',
  // cloud (5)
  'connect_ban_evasion',
  'connect_missing_identifier',
  'connect_vpn',
  'connect_name_filter',
  'connect_low_trust',
] as const

export const DetectionKeySchema = z.enum(DETECTION_KEYS)
export type DetectionKey = (typeof DETECTION_KEYS)[number]

export interface DetectionDefaults {
  enabled: boolean
  action: Action
  /** Positive samples inside `windowMs` before the module trips. */
  strikes: number
  windowMs: number
  /** Request a screenshot when this module trips. */
  screenshot: boolean
}

export interface DetectionMeta {
  side: Side
  category: Category
  severity: Severity
  /**
   * No legitimate path produces this signal, so it may back a global ban
   * proposal (SPEC.md §10.6).
   */
  deterministic: boolean
  /** A server-side getter exists, so a client claim can be cross-checked before a ban. */
  serverVerifiable: boolean
  /** The panel cannot change the action of these modules. */
  lockedAction?: Action
  defaults: DetectionDefaults
}

export { DETECTION_META }

export const CLIENT_DETECTION_KEYS: DetectionKey[] = DETECTION_KEYS.filter((k) => DETECTION_META[k].side === 'client')
export const SERVER_DETECTION_KEYS: DetectionKey[] = DETECTION_KEYS.filter((k) => DETECTION_META[k].side === 'server')
export const CLOUD_DETECTION_KEYS: DetectionKey[] = DETECTION_KEYS.filter((k) => DETECTION_META[k].side === 'cloud')

export function isDetectionKey(value: unknown): value is DetectionKey {
  return typeof value === 'string' && (DETECTION_KEYS as readonly string[]).includes(value)
}

export function detectionsByCategory(): Record<Category, DetectionKey[]> {
  const out = Object.fromEntries(CATEGORIES.map((c) => [c, [] as DetectionKey[]])) as Record<Category, DetectionKey[]>
  for (const key of DETECTION_KEYS) {
    const category: Category = DETECTION_META[key].category
    out[category].push(key)
  }
  return out
}

/**
 * SPEC.md §1.2 — the action ladder.
 *
 * A configured `ban` on a module the server can verify is downgraded to `kick`
 * unless the server actually confirmed the signal, and `general.logOnly` forces
 * every action down to `log`. This is the single place that rule lives.
 */
export function resolveAction(
  key: DetectionKey,
  configured: Action,
  context: { logOnly: boolean; serverConfirmed: boolean },
): Action {
  if (context.logOnly) return 'log'
  const meta = DETECTION_META[key]
  if (meta.lockedAction) return meta.lockedAction
  if (configured === 'ban' && meta.serverVerifiable && !context.serverConfirmed) return 'kick'
  return configured
}

/** Severity ordering, used by webhook `minSeverity` filters and panel sorting. */
export const SEVERITY_RANK: Record<Severity, number> = { low: 0, medium: 1, high: 2, critical: 3 }

export function severityAtLeast(actual: Severity, minimum: Severity): boolean {
  return SEVERITY_RANK[actual] >= SEVERITY_RANK[minimum]
}
